/**
 * useEmotionSocket Hook
 * Manages Socket.IO connection to emotion detection service
 */
import { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
const EMOTION_SERVICE_URL = 'https://app.nor-dmt.no';
const HISTORY_LENGTH = 30; // Keep 30 seconds of history
export function useEmotionSocket(sessionId, ownParticipantId) {
    const [connected, setConnected] = useState(false);
    const [participants, setParticipants] = useState(new Map());
    const [error, setError] = useState(null);
    const socketRef = useRef(null);
    // Initialize Socket.IO connection
    useEffect(() => {
        // Skip connection if no sessionId (user is not a therapist)
        if (!sessionId) {
            console.log('[useEmotionSocket] No sessionId - skipping connection');
            return;
        }
        // Silently attempt connection - emotion service may not be running
        const socket = io(EMOTION_SERVICE_URL, {
            path: '/socket.io/',
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: 3,
            reconnectionDelay: 2000,
            reconnectionDelayMax: 10000,
            timeout: 5000
        });
        socketRef.current = socket;
        // Connection established
        socket.on('connect', () => {
            setConnected(true);
            setError(null);
            // Join session as therapist
            socket.emit('join-session', {
                sessionId,
                role: 'therapist',
                participantId: ownParticipantId
            });
        });
        // Session joined confirmation
        socket.on('session-joined', () => {
            // Request current session state
            socket.emit('request-session-state', { sessionId });
        });
        // Session state update (list of participants)
        socket.on('session-state-update', (data) => {
            if (data.participants) {
                setParticipants(prev => {
                    const updated = new Map(prev);
                    data.participants.forEach((p) => {
                        if (!updated.has(p.participantId) && p.participantId !== ownParticipantId) {
                            updated.set(p.participantId, {
                                id: p.participantId,
                                name: p.participantName || 'Unnamed Patient',
                                currentEmotions: null,
                                history: [],
                                lastUpdate: null,
                                latency: 0
                            });
                        }
                    });
                    return updated;
                });
            }
        });
        // Emotion update from a participant
        socket.on('emotion-update', (data) => {
            // Filter out therapist's own emotions
            if (data.participantId === ownParticipantId || data.participantId.startsWith('therapist-')) {
                return;
            }
            // Emotion update received
            setParticipants(prev => {
                const updated = new Map(prev);
                // Add participant if not exists
                if (!updated.has(data.participantId)) {
                    updated.set(data.participantId, {
                        id: data.participantId,
                        name: data.participantName || 'Unnamed Patient',
                        currentEmotions: null,
                        history: [],
                        lastUpdate: null,
                        latency: 0
                    });
                }
                const participant = updated.get(data.participantId);
                // Update current emotions
                participant.currentEmotions = data.emotions;
                participant.lastUpdate = data.timestamp;
                participant.latency = data.latency || 0;
                // Add to history
                participant.history.push({
                    timestamp: data.timestamp,
                    emotions: data.emotions
                });
                // Trim history
                if (participant.history.length > HISTORY_LENGTH) {
                    participant.history.shift();
                }
                return updated;
            });
        });
        // Disconnection
        socket.on('disconnect', () => {
            setConnected(false);
        });
        // Connection error - only log once, don't spam
        let errorLogged = false;
        socket.on('connect_error', (err) => {
            if (!errorLogged) {
                console.warn('[useEmotionSocket] Connection error (emotion service may not be running):', err.message);
                errorLogged = true;
            }
            setError('Emotion service unavailable');
            setConnected(false);
        });
        // Failed to reconnect after all attempts
        socket.on('reconnect_failed', () => {
            console.warn('[useEmotionSocket] Reconnection failed - emotion service unavailable');
            setError('Emotion service unavailable');
            setConnected(false);
        });
        // Reconnection
        socket.on('reconnect', () => {
            errorLogged = false;
            setConnected(true);
            setError(null);
            // Re-request session state
            socket.emit('request-session-state', { sessionId });
        });
        // Cleanup on unmount
        return () => {
            socket.disconnect();
        };
    }, [sessionId, ownParticipantId]);
    return {
        connected,
        participants,
        error
    };
}
